import React, { useState, useRef } from 'react';
import { MapPin, Mail, Clock, CheckCircle, Send } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import emailjs from '@emailjs/browser';
import NewsletterSection from '../components/NewsletterSection';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const ContactPage = () => {
  const formRef = useRef(null);
  const recaptchaRef = useRef(null);

  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Full name is required.';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required.';
    if (!captchaToken) newErrors.captcha = 'Please complete the CAPTCHA.';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, formRef.current, EMAILJS_PUBLIC_KEY);
      setFormSubmitted(true);
    } catch (err) {
      setSubmitError('Something went wrong. Please try again or email us directly at support@realmoflove.org');
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({ name: '', email: '', subject: '', message: '' });
    setErrors({});
    setCaptchaToken(null);
    setSubmitError('');
    setFormSubmitted(false);
    recaptchaRef.current?.reset();
  };

  return (
    <>
      <section style={{ paddingTop: '120px', paddingBottom: '4rem', background: 'var(--off-white)' }}>
        <div className="container">
          <div className="section-header" style={{ marginBottom: '2rem' }}>
            <span className="section-tag">Get in Touch</span>
            <h1>Contact Us</h1>
            <p>We'd love to hear from you. Reach out for partnerships, volunteering, or any enquiries.</p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact-grid">
            {/* Form */}
            <div className="scroll-animate card" style={{ padding: '2.5rem' }}>
              {formSubmitted ? (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                  <CheckCircle size={56} style={{ color: 'var(--teal)', marginBottom: '1rem' }} />
                  <h3 style={{ marginBottom: '0.5rem' }}>Message Sent!</h3>
                  <p>Thank you for reaching out. We'll get back to you within 48 hours.</p>
                  <button className="btn btn-secondary" style={{ marginTop: '1.5rem' }} onClick={handleReset}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h3 style={{ marginBottom: '1.5rem' }}>Send us a Message</h3>
                  <form ref={formRef} onSubmit={handleSubmit} noValidate>
                    <div className="contact-form-row">
                      <div className="form-group">
                        <label className="form-label">Full Name *</label>
                        <input
                          className="form-input"
                          type="text"
                          name="from_name"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={(e) => handleChange({ target: { name: 'name', value: e.target.value } })}
                        />
                        {errors.name && <span style={{ color: 'var(--rose)', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>{errors.name}</span>}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email *</label>
                        <input
                          className="form-input"
                          type="email"
                          name="from_email"
                          placeholder="your@email.com"
                          value={formData.email}
                          onChange={(e) => handleChange({ target: { name: 'email', value: e.target.value } })}
                        />
                        {errors.email && <span style={{ color: 'var(--rose)', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>{errors.email}</span>}
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Subject</label>
                      <select
                        className="form-select"
                        name="subject"
                        value={formData.subject}
                        onChange={(e) => handleChange(e)}
                      >
                        <option value="">Select a topic</option>
                        <option value="Volunteering">Volunteering</option>
                        <option value="Partnership">Partnership</option>
                        <option value="Donation Enquiry">Donation Enquiry</option>
                        <option value="Programme Information">Programme Information</option>
                        <option value="Media & Press">Media &amp; Press</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Message *</label>
                      <textarea
                        className="form-textarea"
                        name="message"
                        placeholder="How can we help?"
                        value={formData.message}
                        onChange={(e) => handleChange(e)}
                      />
                      {errors.message && <span style={{ color: 'var(--rose)', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>{errors.message}</span>}
                    </div>

                    <div className="form-group">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(token) => { setCaptchaToken(token); setErrors(prev => ({ ...prev, captcha: '' })); }}
                        onExpired={() => setCaptchaToken(null)}
                      />
                      {errors.captcha && <span style={{ color: 'var(--rose)', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>{errors.captcha}</span>}
                    </div>

                    {submitError && (
                      <div style={{
                        background: 'rgba(var(--rose-rgb, 220,53,69),0.08)',
                        border: '1px solid var(--rose)',
                        borderRadius: '8px',
                        padding: '0.75rem 1rem',
                        color: 'var(--rose)',
                        fontSize: '0.875rem',
                        marginBottom: '1rem'
                      }}>
                        {submitError}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      style={{ width: '100%' }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span style={{
                            display: 'inline-block', width: '16px', height: '16px',
                            border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff',
                            borderRadius: '50%', animation: 'spin 0.7s linear infinite', marginRight: '8px'
                          }} />
                          Sending...
                        </>
                      ) : (
                        <><Send size={18} /> Send Message</>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <div className="scroll-animate card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
                <h4 style={{ marginBottom: '1.5rem' }}>Contact Information</h4>
                {[
                  { icon: <MapPin size={18} />, label: 'Address', value: 'Johannesburg, Gauteng, South Africa' },
                  { icon: <Mail size={18} />, label: 'Email', value: 'support@realmoflove.org' },
                  { icon: <Clock size={18} />, label: 'Hours', value: 'Mon-Fri: 8am - 5pm' }
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '10px',
                      background: 'rgba(78,185,191,0.1)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: 'var(--teal)', flexShrink: 0
                    }}>
                      {item.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', fontWeight: 500 }}>{item.label}</div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--gray-700)' }}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <NewsletterSection />
    </>
  );
};

export default ContactPage;
